import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, RefreshCw, Trash2 } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

const STATUS_META = {
  open: { label: "Waiting", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  notified: { label: "Notified", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  closed: { label: "Closed", cls: "border-[#B9C8D5]/30 bg-black/25 text-[#B9C8D5]" },
};

export const StockRequests = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stock-requests`, { params: filter ? { status: filter } : {} });
      setRows(data.requests || []);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Couldn't load stock requests");
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/stock-requests/${id}`, { status });
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await axios.delete(`${API}/admin/stock-requests/${id}`);
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Delete failed"); }
  };
  const runCheck = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/stock-requests/run-check`);
      toast.success(`Checked availability — ${data.notified} guest${data.notified === 1 ? "" : "s"} notified.`);
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Check failed"); }
  };

  return (
    <div className="space-y-6" data-testid="stock-requests-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Sold-out gear</p>
          <h1 className="font-serif text-3xl text-[#F6F1E4]">Stock Requests</h1>
          <p className="text-sm text-[#B9C8D5] mt-1">Guests who tapped "Request it" on a sold-out item. They're emailed automatically (hourly check) when a unit frees up for their dates.</p>
        </div>
        <Btn onClick={runCheck} variant="ghost" data-testid="stock-requests-run-check"><RefreshCw className="w-4 h-4 mr-1 inline" /> Check now</Btn>
      </div>

      <div className="flex gap-2" data-testid="stock-requests-filters">
        {[["open", "Waiting"], ["notified", "Notified"], ["closed", "Closed"], ["", "All"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setFilter(v)} data-testid={`stock-requests-filter-${v || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filter === v ? "bg-[#FFD166] text-[#040A0F] border-[#FFD166]" : "border-[#FFD166]/30 text-[#FFD166] hover:bg-[#FFD166]/10"}`}>
            {l}
          </button>
        ))}
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="stock-requests-table">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#B9C8D5] border-b border-[#FFD166]/15">
            <tr>
              <th className="px-4 py-3">Requested</th>
              <th className="px-4 py-3">Item</th>
              <th className="px-4 py-3">Guest</th>
              <th className="px-4 py-3">Dates</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#B9C8D5]" data-testid="stock-requests-empty">
                {loading ? "Loading…" : <><BellRing className="w-5 h-5 inline mr-2 text-[#FFD166]" />No stock requests here.</>}
              </td></tr>
            )}
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.open;
              return (
                <tr key={r.id} className="border-b border-[#FFD166]/10 hover:bg-white/[0.02]" data-testid={`stock-request-row-${r.id}`}>
                  <td className="px-4 py-3 text-[#B9C8D5] whitespace-nowrap">{String(r.created_at).slice(0, 10)}</td>
                  <td className="px-4 py-3 text-[#F6F1E4] font-medium">{r.product_name}</td>
                  <td className="px-4 py-3">
                    <div className="text-[#F6F1E4]">{r.name}</div>
                    <div className="text-xs text-[#B9C8D5]"><a href={`mailto:${r.email}`} className="hover:text-[#FFD166]">{r.email}</a>{r.phone ? ` · ${r.phone}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-[#F6F1E4]">{r.start_date} → {r.end_date}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] border ${meta.cls}`} data-testid={`stock-request-status-${r.id}`}>{meta.label}</span>
                    {r.notified_at && <div className="text-[10px] text-[#B9C8D5] mt-1">emailed {String(r.notified_at).slice(0, 10)}</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.status !== "closed" && (
                      <button type="button" onClick={() => setStatus(r.id, "closed")} className="text-xs text-[#B9C8D5] hover:text-[#FFD166] mr-3" data-testid={`stock-request-close-${r.id}`}>Close</button>
                    )}
                    {r.status === "closed" && (
                      <button type="button" onClick={() => setStatus(r.id, "open")} className="text-xs text-[#B9C8D5] hover:text-[#FFD166] mr-3" data-testid={`stock-request-reopen-${r.id}`}>Reopen</button>
                    )}
                    <button type="button" onClick={() => remove(r.id)} className="text-rose-300 hover:text-rose-200" aria-label="Delete" data-testid={`stock-request-delete-${r.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default StockRequests;
